<template>
  <div
    id="dropzone"
    ref="dropzone"
    class="w-full md:p-5 rounded-sm text-center border-2 border-dashed"
    :class="[isDraggingOver ? 'bg-blue-100/50 border-blue-500' : `${isMobile
      ? 'bg-white border-white'
      : 'bg-gray-50 border-gray-50'}`
    ]"
    @drop.prevent="handleDrop"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent="() => {}"
  >
    <div class="md:inline-block space-y-3">
      <span v-if="!isMobile" class="mr-1.5 md:mr-0 font-normal">
        <FaIcon name="upload" size="lg" fixed-width />
        Dateien hier ablegen oder</span>
      <label
        for="file-upload-button"
        class="mb-0 pb-0 w-full md:w-auto text-left"
      >
        <button
          class="
            font-normal text-gray-900 bg-white md:bg-gray-50 py-2.5 md:py-1 px-2 hover:bg-gray-100 rounded-sm
            w-full md:w-auto text-left md:text-blue-500 md:underline text-base md:text-sm
          "
          data-testid="auswählen-button"
          @click.prevent="$refs.fileInput.click()"
        >
          <FaIcon v-if="isMobile" name="folder-open" variant="light" size="lg" fixed-width class="mr-3" />
          {{ isMobile ? 'Dateien ' : '' }}auswählen
        </button>
        <input
          id="file-upload-button"
          ref="fileInput"
          type="file"
          :accept="accept"
          class="hidden"
          multiple
          @dragover.prevent="() => {}"
          @dragleave.prevent="() => {}"
          @change="(ev) => onInput(ev.target.files)"
          @drop.prevent="(ev) => onInput(ev.target.files)"
        >
      </label>
    </div>
    <div v-if="isMobile" class="border-t border-gray-100 my-1" />
    <div v-if="isMobile" class="md:inline-block">
      <label
        for="capture-button"
        class="mb-0 pb-0 w-full inline-block text-left"
      >
        <button
          class="font-normal text-gray-900 text-base bg-white md:bg-gray-100 py-2.5 px-2 hover:bg-gray-100 rounded-sm w-full md:w-auto text-left"
          data-testid="auswählen-button"
          @click.prevent="$refs.cameraInput.click()"
        >
          <FaIcon name="camera" variant="light" size="lg" fixed-width class="mr-3" />
          Foto aufnehmen
        </button>
        <input
          id="capture-button"
          ref="cameraInput"
          type="file"
          accept="image/*"
          capture="environment"
          class="hidden"
          multiple
          @dragover.prevent="() => {}"
          @dragleave.prevent="() => {}"
          @change="(ev) => onInput((ev.target as HTMLInputElement).files)"
          @drop.prevent="(ev) => onInput((ev.target as HTMLInputElement).files)"
        >
      </label>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ref } from 'vue';
import { useDetectMobile } from "@/application/composables/detectMobile";

defineProps({
  accept: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['input']);

const { isMobile } = useDetectMobile();

const fileInput = ref<HTMLInputElement | null>(null);
const cameraInput = ref<HTMLInputElement | null>(null);
const isDraggingOver = ref(false);
const dropzone = ref<HTMLElement | null>(null);

const onInput = (files: FileList|null) => {
  if (files === null) return;

  emit('input', files);
  if (cameraInput.value) cameraInput.value.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

function handleDragEnter() {
  isDraggingOver.value = true;
}

function handleDragLeave(event: DragEvent) {
  if (!dropzone.value || !event.relatedTarget || !dropzone.value.contains(event.relatedTarget as Node)) {
    isDraggingOver.value = false;
  }
}

function handleDrop(event: DragEvent) {
  isDraggingOver.value = false;
  if (!event.dataTransfer) return;
  onInput(event.dataTransfer.files)
}
</script>
